<template>
  <div class="p-6">
    <Accordion v-model:activeIndex="activeIndex">
      <AccordionTab v-for="tournament in tournaments" :key="tournament.id">
        <!-- Header slot -->
        <template #header> 🏆 {{ tournament.naziv }} </template>

        <!-- Accordion content -->
        <div>
          <!-- Your tournament details here -->
        </div>

        <div v-if="tournament.matches">
          <div
            v-for="(groups, phaseName) in filteredPhases(tournament)"
            :key="phaseName"
            class="mb-8"
          >
            <h4 class="text-xl font-semibold mb-4">{{ phaseName }}</h4>

            <div class="row">
              <div
                v-for="(matches, groupName) in groups"
                :key="groupName"
                class="col-md-6 rounded-lg p-4 bg-gray-50"
              >
                <h5 class="text-lg font-bold mb-2">{{ groupName }}</h5>

                <!-- Standings table only for non-playoff -->
                <table
                  v-if="!isPlayoffPhase(phaseName)"
                  class="table-auto w-full mb-2 border-collapse"
                >
                  <thead>
                    <tr class="bg-gray-200">
                      <th class="border px-2 py-1 text-center">Pos</th>
                      <th class="border px-2 py-1 text-left">Player</th>
                      <th class="border px-2 py-1 text-center">Matches</th>
                      <th class="border px-2 py-1 text-center">Won</th>
                      <th class="border px-2 py-1 text-center">
                        Sets<br />Won
                      </th>
                      <th class="border px-2 py-1 text-center">
                        Sets<br />Lost
                      </th>
                      <th class="border px-2 py-1 text-center">
                        Games<br />Diff
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(player, index) in getStandings(matches)"
                      :key="player.name"
                      :class="[
                        'hover:bg-gray-100',
                        phaseName === 'Group Stage 1'
                          ? getMedalClass(
                              tournament.id,
                              index,
                              getStandings(matches).length,
                              phaseName,
                            )
                          : '',
                        phaseName === 'Group Stage 2'
                          ? getMedalClassPhase2(index, tournament.id, groupName)
                          : '',
                      ]"
                    >
                      <td class="border px-2 py-1 text-center">
                        {{ index + 1 }}.
                      </td>
                      <td class="border px-2 py-1">{{ player.name }}</td>
                      <td class="border px-2 py-1 text-center">
                        {{ player.played }}
                      </td>
                      <td class="border px-2 py-1 text-center">
                        {{ player.won }}
                      </td>
                      <td class="border px-2 py-1 text-center">
                        {{ player.setsWon }}
                      </td>
                      <td class="border px-2 py-1 text-center">
                        {{ player.setsLost }}
                      </td>
                      <td class="border px-2 py-1 text-center">
                        {{ player.scoreDiff }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Toggle matches button only for group stages -->
                <button
                  v-if="!isPlayoffPhase(phaseName)"
                  @click="toggleMatches(tournament.id, groupName)"
                  class="btn btn-small btn-primary mb-2"
                >
                  {{
                    showMatches[tournament.id]?.[groupName]
                      ? "Sakrij mečeve"
                      : "Prikaži mečeve"
                  }}
                </button>

                <!-- Matches table -->
                <div
                  v-if="
                    showMatches[tournament.id]?.[groupName] ||
                    isPlayoffPhase(phaseName)
                  "
                  class="mt-2"
                >
                  <div
                    v-for="(
                      roundMatches, roundName
                    ) in groupMatchesByGroupAndRound({ [groupName]: matches })[
                      groupName
                    ]"
                    :key="roundName"
                    class="mb-4"
                  >
                    <h5 class="font-semibold mb-2">{{ roundName }}</h5>

                    <table
                      class="table-auto w-full border-collapse"
                      style="font-size: 90%"
                    >
                      <thead>
                        <tr class="bg-gray-200" style="display: none">
                          <th class="border px-2 py-1" style="width: 33%">
                            Player 1
                          </th>
                          <th class="border px-2 py-1" style="width: 33%">
                            Player 2
                          </th>
                          <th class="border px-2 py-1" style="width: 33%">
                            Sets
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="m in roundMatches"
                          :key="m.match_id"
                          class="hover:bg-gray-100 align-top"
                        >
                          <td
                            v-if="!isSlobodanMatch(m)"
                            class="border px-2 py-1"
                          >
                            <span
                              :class="{ 'font-bold': isWinner(m, m.player1) }"
                              >{{ m.player1 }}</span
                            >
                          </td>
                          <td
                            v-if="!isSlobodanMatch(m)"
                            class="border px-2 py-1"
                          >
                            <span
                              :class="{ 'font-bold': isWinner(m, m.player2) }"
                              >{{ m.player2 }}</span
                            >
                          </td>
                          <td
                            v-if="!isSlobodanMatch(m)"
                            class="border px-2 py-1"
                            style="word-wrap: nowrap; min-width: 100px"
                          >
                            <div class="prikazRezultata">
                              <span v-if="m.sets.length">
                                <span v-for="(s, i) in m.sets" :key="i"
                                  >{{ s.rezultat_1 }}:{{
                                    s.rezultat_2
                                  }}&nbsp;</span
                                >
                              </span>

                              <!-- Edit icon -->
                              <i
                                v-if="isAdmin"
                                class="pi pi-pencil text-blue-600 cursor-pointer"
                                @click="
                                  openEditModal(
                                    m.match_id,
                                    m.player1,
                                    m.player2,
                                  )
                                "
                                title="Edit results"
                              ></i>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AccordionTab>
    </Accordion>
  </div>

  <div class="row">
    <div class="col-md-3 text-center">
      <a href="https://www.vina-vivoda.hr/" target="_blank">
        <img class="vinalogo" src="images/Vivida_logo novi.jpg"
      /></a>
    </div>
    <div class="col-md-3 text-center">
      <a href="https://shop.chiavalon.hr/" target="_blank"
        ><img
          class="vinalogo"
          src="images/Chiavalon_logo.png"
          style="max-width: 200px"
      /></a>
    </div>
    <div class="col-md-3 text-center">
      <a href="https://teniswil.hr/" target="_blank"
        ><img class="vinalogo" src="images/teniswil.jpg"
      /></a>
    </div>
    <div class="col-md-3 text-center">
      <a href="https://tecro24.com/" target="_blank"
        ><img class="vinalogo" src="images/tecro.jpg"
      /></a>
    </div>
  </div>

  <Dialog
    v-model:visible="showEditModal"
    modal
    class="edit-dialog"
    header="Unos/promjena rezultata"
    :style="{ width: '400px' }"
  >
    <hr />
    <div v-if="selectedMatch">
      <!-- Player Names -->
      <div class="mb-3 text-center">
        <strong>{{ player1 }}</strong>
        -
        <strong>{{ player2 }}</strong>
      </div>

      <!-- Inputs in one row -->
      <!-- Container for all sets in one row -->
      <div class="flex gap-6 mb-3">
        <template v-for="n in 3" :key="n">
          <!-- Each column -->
          <div class="flex-1 flex flex-col items-center mb-3">
            <!-- Title -->
            <span class="font-semibold mb-1">Set {{ n }}</span>

            <!-- Inputs -->
            <div class="flex items-center gap-1">
              <InputNumber
                v-model="selectedMatch[`set${n}_p1`]"
                :inputStyle="{ width: '40px', textAlign: 'center' }"
                max="99"
                min="0"
                :showButtons="false"
                :format="false"
              />
              <span style="margin: 6px">:</span>
              <InputNumber
                v-model="selectedMatch[`set${n}_p2`]"
                :inputStyle="{ width: '40px', textAlign: 'center' }"
                max="99"
                min="0"
                :showButtons="false"
                :format="false"
              />
            </div>
          </div>
        </template>
      </div>

      <!-- Save button -->
      <Button
        label="Save"
        class="btn btn-small btn-primary mb-2"
        @click="saveMatchResults"
      />
    </div>
  </Dialog>
</template>

<script>
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Dialog from "primevue/dialog";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";

export default {
  components: { Accordion, AccordionTab, Dialog, InputNumber, Button },
  data() {
    return {
      tournaments: [],
      activeIndex: null,
      showMatches: {},
      baseUrl: localStorage.getItem("_rootRestUrl"),
      isAdmin: false,
      showEditModal: false,
      selectedMatch: {},
      player1: "",
      playe2: "",
    };
  },
  async mounted() {
    // Fetch all tournaments
    const res = await fetch(`${this.baseUrl}turniri-popisTurnira.php`);
    const data = await res.json();
    this.tournaments = data.map((t) => ({ ...t, matches: null }));

    if (
      this.$store.getters.getUserType === "17b626dd5cc94bad40ae995fbb3c44dc" // md5 od "OnJeAdmin!aliMD5OtToga" iz login.php
    ) {
      this.isAdmin = true;
    }
  },
  watch: {
    activeIndex(newIndex) {
      if (newIndex !== null) this.loadTournamentMatches(newIndex);
    },
  },
  methods: {
    openEditModal(matchId, player1, player2) {
      this.player1 = player1;
      this.player2 = player2;

      matchId = parseInt(matchId, 10);
      let foundMatch = null;

      // Search all tournaments
      for (const t of this.tournaments) {
        if (!t.matches?.phases) continue;

        // Search phases
        for (const phaseName in t.matches.phases) {
          const phase = t.matches.phases[phaseName];

          // Search groups (A, B, C...)
          for (const groupName in phase) {
            const matches = phase[groupName];

            const found = matches.find((mm) => mm.match_id == matchId);

            if (found) {
              foundMatch = JSON.parse(JSON.stringify(found));

              // Ensure always 3 sets and copy results to selectedMatch
              for (let n = 1; n <= 3; n++) {
                const set = foundMatch.sets[n - 1] || {
                  rezultat_1: "",
                  rezultat_2: "",
                };

                //i got here correct  set.rezultat_1 and  set.rezultat_2

                this.selectedMatch["matchID"] = matchId;

                this.selectedMatch[`set${n}_p1`] = set.rezultat_1;
                this.selectedMatch[`set${n}_p2`] = set.rezultat_2;
                // Fill in missing sets if necessary
                if (!foundMatch.sets[n - 1])
                  this.selectedMatch[`set${n}_p2`] = "";
              }

              break;
            }
          }
          if (foundMatch) break;
        }
        if (foundMatch) break;
      }

      if (!foundMatch) {
        console.error("❌ Match NOT FOUND:", matchId);
        return;
      }

      // Always ensure 3 sets
      for (let i = 1; i <= 3; i++) {
        if (!foundMatch.sets[i - 1]) {
          foundMatch.sets[i - 1] = {
            set: i,
            rezultat_1: "",
            rezultat_2: "",
          };
        }
      }

      this.showEditModal = true; // <-- FIXED
    },
    async saveMatchResults() {
      // Prepare the data to send
      const payload = {
        match_id: this.selectedMatch["matchID"],
        set1_p1: this.selectedMatch[`set1_p1`],
        set1_p2: this.selectedMatch[`set1_p2`],
        set2_p1: this.selectedMatch[`set2_p1`],
        set2_p2: this.selectedMatch[`set2_p2`],
        set3_p1: this.selectedMatch[`set3_p1`],
        set3_p2: this.selectedMatch[`set3_p2`],
      };

      // Close modal immediately
      this.showEditModal = false;

      try {
        const res = await fetch(`${this.baseUrl}turniri-spremiRezultat.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.getters.token}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        console.log("API response:", data);

        // After saving, reload the tournament matches
        if (this.activeIndex !== null) {
          await this.loadTournamentMatchesafterEdit(this.activeIndex);
        }
      } catch (err) {
        console.error("Error saving match results:", err);
      }
    },
    async loadTournamentMatches(index) {
      const tournament = this.tournaments[index];
      if (!tournament.matches) {
        const res = await fetch(
          `${this.baseUrl}turniri_dajMiTurnir.php?id=${tournament.id}`,
        );
        const data = await res.json();
        this.tournaments[index] = { ...tournament, matches: data };
      }
    },

    async loadTournamentMatchesafterEdit(index) {
      const tournament = this.tournaments[index];

      const res = await fetch(
        `${this.baseUrl}turniri_dajMiTurnir.php?id=${tournament.id}`,
      );
      const data = await res.json();
      this.tournaments[index] = { ...tournament, matches: data };
    },
    toggleMatches(tournamentId, groupName) {
      if (!this.showMatches[tournamentId]) this.showMatches[tournamentId] = {};
      this.showMatches[tournamentId][groupName] =
        !this.showMatches[tournamentId][groupName];
    },
    isPlayoffPhase(phaseName) {
      return phaseName.toLowerCase().includes("playoff");
    },
    isSlobodanMatch(m) {
      return (
        m.virtual_id1?.toLowerCase() === "[slobodan]" ||
        m.virtual_id2?.toLowerCase() === "[slobodan]"
      );
    },
    isWinner(match, playerName) {
      return (
        match.winner?.trim().toLowerCase() === playerName?.trim().toLowerCase()
      );
    },
    getMedalClass(tournamentId, index, totalPlayers) {
      const pos = index + 1;

      // --- TOURNAMENT 1 ---
      if (tournamentId === 1) {
        if (totalPlayers >= 7) {
          if (pos <= 4) return "text-yellow"; // gold (top 3)
          return "text-gray"; // bronze
        } else {
          if (pos <= 3) return "text-yellow"; // gold
          return "text-gray"; // silver
        }
      }

      // --- TOURNAMENT 2 ---
      if (tournamentId === 2) {
        // If 7+ players → different logic
        if (totalPlayers >= 7) {
          if (pos <= 3) return "text-yellow"; // gold (top 3)
          if (pos <= 5) return "text-gray"; // silver (next 2)
          return "text-amber"; // bronze
        }

        // Normal (6 or fewer players)
        if (pos <= 2) return "text-yellow "; // gold
        if (pos <= 4) return "text-gray "; // silver
        return "text-amber"; // bronze
      }

      // --- TOURNAMENT 3 & 4 ---
      if (tournamentId === 3) {
        if (pos <= 2) return "text-yellow"; // gold
        return "text-gray"; // silver
      }

      // --- TOURNAMENT 3 & 4 ---
      if (tournamentId === 4) {
        if (pos <= 3) return "text-yellow"; // gold
        return "text-gray"; // silver
      }

      return "";
    },
    getMedalClassPhase2(index, tournamentId, groupName) {
      const pos = index + 1;

      // --- TOURNAMENT 1 ---

      if (tournamentId === 2 && groupName === "Gold Group") {
        if (pos <= 2) return "text-yellow"; // gold
        if (pos > 2 && pos <= 6) return "text-yellow-2"; // gold
        return ""; // silver
      } else {
        if (pos <= 4) {
          return "text-yellow";
        } else {
          return "";
        }
      }
    },
    getStandings(matches) {
      const stats = {};
      const h2h = {}; // head-to-head winner map

      matches.forEach((m) => {
        if (
          m.player1?.toLowerCase().includes("[slobodan]") ||
          m.player2?.toLowerCase().includes("[slobodan]")
        )
          return;

        if (!stats[m.player1]) {
          stats[m.player1] = {
            name: m.player1,
            played: 0,
            won: 0,
            setsWon: 0,
            setsLost: 0,
            scoreDiff: 0,
          };
        }

        if (!stats[m.player2]) {
          stats[m.player2] = {
            name: m.player2,
            played: 0,
            won: 0,
            setsWon: 0,
            setsLost: 0,
            scoreDiff: 0,
          };
        }

        if (!m.sets || m.sets.length === 0) return;

        let score1 = 0,
          score2 = 0;

        m.sets.forEach((s) => {
          score1 += s.rezultat_1;
          score2 += s.rezultat_2;
        });

        stats[m.player1].played++;
        stats[m.player2].played++;

        stats[m.player1].setsWon += m.sets_won_p1;
        stats[m.player1].setsLost += m.sets_won_p2;

        stats[m.player2].setsWon += m.sets_won_p2;
        stats[m.player2].setsLost += m.sets_won_p1;

        stats[m.player1].scoreDiff += score1 - score2;
        stats[m.player2].scoreDiff += score2 - score1;

        if (m.winner && !m.winner.toLowerCase().includes("[slobodan]")) {
          stats[m.winner].won++;

          // store head-to-head
          const loser = m.winner === m.player1 ? m.player2 : m.player1;
          if (!h2h[m.winner]) h2h[m.winner] = {};
          h2h[m.winner][loser] = true;
        }
      });

      // existing sort logic (unchanged)
      const defaultSort = (a, b) => {
        if (a.won !== b.won) return b.won - a.won;

        const aSetDiff = a.setsWon - a.setsLost;
        const bSetDiff = b.setsWon - b.setsLost;
        if (aSetDiff !== bSetDiff) return bSetDiff - aSetDiff;

        if (a.setsWon !== b.setsWon) return b.setsWon - a.setsWon;
        if (a.scoreDiff !== b.scoreDiff) return b.scoreDiff - a.scoreDiff;

        return 0;
      };

      const players = Object.values(stats);

      // group players by number of wins
      const groups = {};
      players.forEach((p) => {
        if (!groups[p.won]) groups[p.won] = [];
        groups[p.won].push(p);
      });

      // final sorted standings
      return Object.keys(groups)
        .sort((a, b) => b - a) // wins DESC
        .flatMap((wins) => {
          const group = groups[wins];

          // 🔥 EXACTLY TWO players → head-to-head decides
          if (group.length === 2) {
            const [p1, p2] = group;

            if (h2h[p1.name]?.[p2.name]) return [p1, p2];
            if (h2h[p2.name]?.[p1.name]) return [p2, p1];

            // fallback if no h2h data
            return group.sort(defaultSort);
          }

          // 🔥 3+ players → keep existing logic
          return group.sort(defaultSort);
        });
    },
    groupMatchesByGroupAndRound(groups) {
      const result = {};
      Object.entries(groups).forEach(([groupName, matches]) => {
        const groupedRounds = {};
        matches.forEach((m) => {
          const roundName = m.round || "1. kolo";
          if (!groupedRounds[roundName]) groupedRounds[roundName] = [];
          groupedRounds[roundName].push(m);
        });
        result[groupName] = groupedRounds;
      });
      return result;
    },
    filteredPhases(tournament) {
      if (!tournament.matches?.phases) return {};
      const filtered = {};
      for (const [phaseName, groups] of Object.entries(
        tournament.matches.phases,
      )) {
        const nonEmptyGroups = Object.fromEntries(
          Object.entries(groups).filter(
            ([, matches]) => matches && matches.length > 0,
          ),
        );
        if (Object.keys(nonEmptyGroups).length > 0)
          filtered[phaseName] = nonEmptyGroups;
      }
      return filtered;
    },
  },
};
</script>

<style scoped>
table {
  border: 1px solid #ccc;
  border-collapse: collapse;
}
.font-bold {
  font-weight: bold;
  color: darkorchid;
}
.standings-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}
.standings-group {
  width: 100%;
}

.unosRezultataInput {
  width: 40px !important;
}
.edit-dialog .p-dialog-header {
  background-color: #eee;
}
.cursor-pointer {
  cursor: pointer;
}
.text-yellow {
  background: #fe03;
  font-weight: 500;
  color: black;
}
.text-yellow-2 {
  background: #78ea0629;
  font-weight: 500;
  color: black;
}

.text-gray {
  background: #eeeeee4f;
  font-weight: 500;
  color: black;
}
.text-amber {
  background: #f90f0f3d;
  font-weight: 500;
  color: black;
}
.vinalogo {
  max-width: 300px;
  margin-top: 30px;
}
</style>
